import React, { useEffect, useMemo, useState } from 'react';
import Table from '../Table';
import { IQuestionnaire } from '../../types/questionnaire';
import Button from '../Button';
import { UserData, JobData } from '../../api/JobsService';
import { FormBuilderService } from '../../api/FormBuilderService';

// VI-579: session-scoped cache for the recruiter "Jobs Attached" counts. Keyed by
// questionnaireId. Persisted to sessionStorage so subsequent visits (tab switches, soft
// refreshes) render with correct numbers immediately on first paint.
const ACTIVE_JOBS_COUNT_CACHE_KEY = 'recruiter:activeJobsCounts:v1';

const readActiveJobsCountCache = (): Record<string, number> => {
  try {
    const raw = sessionStorage.getItem(ACTIVE_JOBS_COUNT_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
};

const writeActiveJobsCountCache = (questionnaireId: string, count: number): void => {
  try {
    const current = readActiveJobsCountCache();
    current[questionnaireId] = count;
    sessionStorage.setItem(ACTIVE_JOBS_COUNT_CACHE_KEY, JSON.stringify(current));
  } catch {
    // sessionStorage may be unavailable (Safari private mode, etc.); silently degrade.
  }
};

interface QuestionnairesSectionProps {
  questionnaires: IQuestionnaire[];
  onAddToJob: (jobNumber: number, itemId: string) => void;
  onPreview: (itemId: string) => void;
  onStatusChange: (questionnaireId: string, newStatus: boolean) => Promise<void>;
  onSetDefault?: (questionnaireId: string) => void;
  onRemoveDefault?: (questionnaireId: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  itemsPerPage: number;
  onItemsPerPageChange: (size: number) => void;
  pageSizeOptions: number[];
  users: Record<number, UserData>;
  loadingUsers: boolean;
  openApiJobs: JobData[];
  jobQuestionnaireMap: Map<number, string>;
  hasDefaultQuestionnaire?: boolean;
  // VI-579 follow-up (Bugs 1 + 3): late-bound updater owned by Home.tsx. We register a
  // real function on this ref's `current` on mount; Home and the Select Job drawer / modals
  // call it to nudge the cached count by a delta or overwrite with an absolute value.
  activeJobsCountUpdaterRef?: React.MutableRefObject<
    (questionnaireId: string, change: { delta?: number; absolute?: number }) => void
  >;
}

const QuestionnairesSection: React.FC<QuestionnairesSectionProps> = ({
  questionnaires,
  onAddToJob,
  onPreview,
  onStatusChange,
  onSetDefault,
  onRemoveDefault,
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  onItemsPerPageChange,
  pageSizeOptions,
  users,
  loadingUsers,
  // VI-579: openApiJobs / jobQuestionnaireMap are no longer used here for the table count.
  // Counts now come from a lazy server-side fetch (see useEffect below) which matches the
  // Reactivate / Preview modal's filter set exactly. Props kept in the interface so upstream
  // callers don't need to change in this MR — they can be pruned in a follow-up cleanup.
  activeJobsCountUpdaterRef,
}) => {
  // VI-579: lazy fetch on mount. Because Home.tsx gates this component behind `loadedTabs`,
  // mount only happens once the user clicks the Questionnaires tab — so the recruiter home's
  // initial render pays no cost from this work. Cached values from sessionStorage hydrate
  // immediately so subsequent visits render with correct numbers on first paint.
  const [activeJobsCounts, setActiveJobsCounts] = useState<Record<string, number | undefined>>(
    () => readActiveJobsCountCache()
  );

  // VI-612: completed screener submission counts for the "Completed" column. `undefined` = still
  // loading (render '-'); number (incl. 0) = loaded. Deliberately NOT sessionStorage-cached like
  // "Jobs Attached": completed counts change from jobseeker activity outside this app, so we refetch
  // the current page's counts on mount / page change to keep them fresh rather than showing a stale
  // cached number.
  const [completedCounts, setCompletedCounts] = useState<Record<string, number | undefined>>({});

  const questionnaireIdsKey = useMemo(
    () => questionnaires.map((q) => q.questionnaireId).sort().join(','),
    [questionnaires]
  );

  useEffect(() => {
    if (questionnaires.length === 0) return;

    const cache = readActiveJobsCountCache();
    const toFetch = questionnaires.filter((q) => cache[q.questionnaireId] === undefined);
    if (toFetch.length === 0) return;

    let cancelled = false;

    toFetch.forEach(async (q) => {
      let resolved = 0;
      try {
        resolved = await FormBuilderService.getActiveJobsCount(q.questionnaireId);
      } catch {
        resolved = 0;
      }
      if (cancelled) return;
      writeActiveJobsCountCache(q.questionnaireId, resolved);
      setActiveJobsCounts((prev) => ({ ...prev, [q.questionnaireId]: resolved }));
    });

    return () => {
      cancelled = true;
    };
    // questionnaireIdsKey is a stable string derived from `questionnaires`; effect re-runs
    // when the set of questionnaires on the current page actually changes (e.g. pagination,
    // status toggle, new questionnaire created).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireIdsKey]);

  // VI-612: lazy fetch of completed submission counts for the questionnaires on the current page.
  // Mirrors the "Jobs Attached" fetch above but fetches fresh (no cache) so newly submitted
  // screeners are reflected. Re-runs when the current page's questionnaire set changes.
  useEffect(() => {
    if (questionnaires.length === 0) return;

    let cancelled = false;

    questionnaires.forEach(async (q) => {
      let resolved = 0;
      try {
        resolved = await FormBuilderService.getCompletedSubmissionCount(q.questionnaireId);
      } catch {
        resolved = 0;
      }
      if (cancelled) return;
      setCompletedCounts((prev) => ({ ...prev, [q.questionnaireId]: resolved }));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireIdsKey]);

  // VI-579 follow-up (Bugs 1 + 3): register the count updater on Home's ref so that any other
  // surface that mutates the data (Select Job drawer attach/detach, View / Reactivate modal's
  // fresh AttachedJobsList fetch, Jobs-tab QuestionnaireAttachmentDrawer) can keep the table's
  // cached number consistent without prop-drilling our setState through several layers.
  //
  // Supports both:
  //   - { delta: ±1 } for optimistic increments after attach / decrements after detach.
  //   - { absolute: N } for the View / Reactivate modal to overwrite the cache with the true
  //     server count it just fetched (catches stale-cache cases from mutations outside this
  //     hook, e.g. Jobs-tab drawer, default auto-attach, job status changes).
  useEffect(() => {
    if (!activeJobsCountUpdaterRef) return;
    activeJobsCountUpdaterRef.current = (questionnaireId, change) => {
      setActiveJobsCounts((prev) => {
        let next: number;
        if (change.absolute !== undefined) {
          next = Math.max(0, change.absolute);
        } else {
          const current = prev[questionnaireId] ?? readActiveJobsCountCache()[questionnaireId] ?? 0;
          next = Math.max(0, current + (change.delta ?? 0));
        }
        writeActiveJobsCountCache(questionnaireId, next);
        return { ...prev, [questionnaireId]: next };
      });
    };
    return () => {
      if (activeJobsCountUpdaterRef) {
        activeJobsCountUpdaterRef.current = () => {};
      }
    };
  }, [activeJobsCountUpdaterRef]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <div className='mt-2 mb-2 text-center'>
      <div className='font-weight-bold py-2 h3'>Questionnaires</div>
      {questionnaires.length === 0 ? (
        <div className="mt-2 mb-2 text-center">
          <img
            src="/svgs/no-results.svg"
            alt="No questionnaires found"
          />
        </div>
      ) : (
        <>
          <div className="d-flex align-items-center mt-3 form-group">
            <label htmlFor="itemsPerPage" className="me-2">Items per page:</label>
            <select
              id="itemsPerPage"
              className="form-control select2 mx-2"
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              style={{ width: 'auto' }}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <Table
            data={questionnaires}
            activeJobsCounts={activeJobsCounts}
            completedCounts={completedCounts}
            activeJobsCountUpdaterRef={activeJobsCountUpdaterRef}
            onAddToJob={onAddToJob}
            onPreview={onPreview}
            onStatusChange={onStatusChange}
            onSetDefault={onSetDefault}
            onRemoveDefault={onRemoveDefault}
            users={users}
            loadingUsers={loadingUsers}
          />
          <div className="d-flex justify-content-center align-items-center mt-3 gap-3">
            <div className="d-flex align-items-center gap-2">
              <Button
                text="Previous"
                variant="outline-primary"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              />
              <span className="mx-2">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                text="Next"
                variant="outline-primary"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QuestionnairesSection; 